// Рендерер, камера, свет. Окружение (небо, земля, пропсы) собирает env.mjs,
// качеством управляет quality.mjs.
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// Порог bloom = 1.0: обычная сцена (белый = яркость 1) НЕ светится, светится
// только то, что мы намеренно пересветили выше единицы — кольцо перфекта,
// золотые конфетти, золотой этаж. Дешевле честного selective bloom в два
// прохода сцены и даёт тот же результат на нашей палитре.
const BLOOM_STRENGTH = 0.5;
const BLOOM_RADIUS = 0.5;
const BLOOM_THRESHOLD = 1.0;

export const COLORS = {
  red: 0xe2231a,      // фирменный красный Этажи
  dark: 0x2b2b2b,     // тёмно-серый
  white: 0xffffff,
  steel: 0xb9c0c7,
};

export function createEngine(canvas) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  // Кинематографичный тонемаппинг: глубже цвета неба и заката, мягче света
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xcfe6f5);

  const camera = new THREE.PerspectiveCamera(
    45, window.innerWidth / window.innerHeight, 0.1, 300
  );
  const aspect = window.innerWidth / window.innerHeight;
  const dist = aspect < 0.8 ? 24 : 16;
  camera.position.set(8, 6, dist);
  camera.lookAt(-1, 2, 0);

  const ambient = new THREE.AmbientLight(0xffffff, 0.4);
  scene.add(ambient);

  // Полусферный свет: небо сверху, отражение земли снизу — объём на low-poly гранях
  const hemi = new THREE.HemisphereLight(0xcfe6f5, 0x86b56a, 0.5);
  scene.add(hemi);

  // Ключевой свет с тенями. Тень «ездит» за башней: frustum держим узким
  // вокруг верхушки, иначе на 100 этажах тени размажутся в кашу.
  const sun = new THREE.DirectionalLight(0xfff4e0, 1.35);
  sun.position.set(14, 22, 10);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.radius = 4;
  sun.shadow.bias = -0.0004;
  sun.shadow.camera.left = -14;
  sun.shadow.camera.right = 14;
  sun.shadow.camera.top = 14;
  sun.shadow.camera.bottom = -14;
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 80;
  scene.add(sun);
  scene.add(sun.target);

  // Контровой свет сзади — отделяет силуэт башни от фона
  const rim = new THREE.DirectionalLight(0xbcd8ff, 0.35);
  rim.position.set(-12, 8, -16);
  scene.add(rim);

  // --- Постобработка: композер собираем один раз, включаем по флагу качества ---
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD
  );
  composer.addPass(bloomPass);
  // OutputPass берёт тонемаппинг и цветовое пространство из рендерера
  composer.addPass(new OutputPass());

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    composer.setSize(window.innerWidth, window.innerHeight);
  });

  /**
   * Кадр. Без bloom рендерим напрямую — композер стоит лишнего прохода,
   * а на слабых устройствах скейлер его выключает первым делом.
   */
  function render(useBloom) {
    if (useBloom) composer.render();
    else renderer.render(scene, camera);
  }

  return { renderer, scene, camera, render, lights: { ambient, hemi, sun, rim } };
}

/**
 * Держит теневой frustum вокруг актуальной верхушки башни.
 * Солнце и его цель едут вверх синхронно — направление света постоянно.
 */
export function followShadow(sun, targetY) {
  const y = Math.max(0, targetY - 3);
  sun.position.y = 22 + y;
  sun.target.position.set(0, y, 0);
  sun.target.updateMatrixWorld();
}
